import { useAutoAnimate } from "@formkit/auto-animate/react";
import { type Breadcrumb, useBreadcrumb } from "components/Breadcrumb";
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Toggle } from "components/ui/Toggle";
import { SEARCH_LIMIT } from "constants/search-fiilter";
import type { EmailSearchInput } from "hooks/useEmailsRestApi";
import type { FC, MouseEventHandler } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { EmailPreviewPane } from "routes/emails/SearchEmailsPage/components/EmailPreviewPane";
import { EmailsCardsUI } from "routes/emails/SearchEmailsPage/components/EmailsCardsUI";
import { EmailsTableUI } from "routes/emails/SearchEmailsPage/components/EmailsTableUI";
import { SearchEmailStats } from "routes/emails/SearchEmailsPage/components/SearchEmailStats";
import { SearchEmailsFormUI } from "routes/emails/SearchEmailsPage/components/SearchEmailsForm";
import { SearchFilterButton } from "routes/emails/SearchEmailsPage/components/SearchFilterButton/SearchFilterButton";
import { useCanShowPreview } from "routes/emails/SearchEmailsPage/hooks/use-preview-layout";
import { SearchConditionsStorage } from "routes/emails/SearchEmailsPage/searchConditionsStorage";
import type {
  EmailsWithStatus,
  EmailWithStatus,
  SelectContextMenuHandler,
} from ".";

type SearchEmailsPageProps = {
  isLoading: boolean;
  isMatter: boolean;
  currentSearchInput: EmailSearchInput;
  invisibleEmailNums: number;
  matterEmails: EmailsWithStatus;
  hrEmails: EmailsWithStatus;
  /** 全画面詳細へ遷移 (=卒業)。狭幅クリック / プレビューの卒業ボタン用 */
  onOpenDetail: (id: string, panel?: "reply") => void;
  /** メールを既読にする (プレビュー選択時) */
  onMarkRead: (id: string) => void;
  onToggleFavoriteEmail: (emailId: string, favorite: boolean) => void;
  onSelectContextMenu: SelectContextMenuHandler;
  onSearch: SubmitHandler<EmailSearchInput>;
  onClearFilter: () => void;
  onShowMore: MouseEventHandler;
};

const BREAD_CRUMBS: Breadcrumb[] = [
  {
    label: "ホーム",
    to: "/",
  },
  {
    label: "メール",
  },
  {
    label: "検索",
  },
];

const PANE_ANIM_MS = 280;

/** Pane の出現/消滅の向き (data-slide="left|right") に応じてスライドさせる useAutoAnimate プラグイン */
type PaneCoords = { top: number; left: number; width: number; height: number };
const slidePaneAnimation = (
  el: Element,
  action: "add" | "remove" | "remain",
  oldCoords?: PaneCoords,
  newCoords?: PaneCoords,
): KeyframeEffect => {
  const dir = (el as HTMLElement).dataset.slide;
  // 出現/消滅とも端からのフルスライド (左Pane=左端 / 右Pane=右端) で対称にする
  const offscreen =
    dir === "left"
      ? "translateX(-100%)"
      : dir === "right"
        ? "translateX(100%)"
        : "translateX(0)";
  let keyframes: Keyframe[];
  if (action === "add") {
    keyframes = [
      { opacity: 0, transform: offscreen },
      { opacity: 1, transform: "translateX(0)" },
    ];
  } else if (action === "remove") {
    keyframes = [
      { opacity: 1, transform: "translateX(0)" },
      { opacity: 0, transform: offscreen },
    ];
  } else {
    const deltaX = (oldCoords?.left ?? 0) - (newCoords?.left ?? 0);
    const deltaY = (oldCoords?.top ?? 0) - (newCoords?.top ?? 0);
    keyframes = [
      { transform: `translate(${deltaX}px, ${deltaY}px)` },
      { transform: "translate(0, 0)" },
    ];
  }
  return new KeyframeEffect(el, keyframes, {
    duration: PANE_ANIM_MS,
    easing: "ease-out",
  });
};

const SearchEmailsPage: FC<SearchEmailsPageProps> = ({
  isLoading,
  isMatter,
  currentSearchInput,
  matterEmails,
  hrEmails,
  invisibleEmailNums,
  onToggleFavoriteEmail,
  onOpenDetail,
  onMarkRead,
  onSelectContextMenu,
  onSearch,
  onClearFilter,
  onShowMore,
}) => {
  const { setBreadcrumbItems } = useBreadcrumb();
  const [searchParams, setSearchParams] = useSearchParams();

  // 表示形式 (URL query parameter `view-type` と同期、未指定時は localStorage から復元)
  const viewModeParam = searchParams.get("view-type");
  const viewMode: "card" | "table" =
    viewModeParam === "card"
      ? "card"
      : viewModeParam === "table"
        ? "table"
        : (SearchConditionsStorage.loadSearchConditions().viewMode ?? "table");
  const viewModeLabel = viewMode === "card" ? "カード表示" : "テーブル表示";
  const setViewMode = useCallback(
    (next: "card" | "table") => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("view-type", next);
      setSearchParams(nextParams, { replace: true });
      SearchConditionsStorage.saveViewMode(next);
    },
    [searchParams, setSearchParams],
  );

  const activeEmails = useMemo(
    () => (isMatter ? matterEmails : hrEmails),
    [isMatter, matterEmails, hrEmails],
  );

  // 詳細プレビューを表示できる画面幅か (lg 以上)。狭幅では全画面遷移にフォールバック。
  const canShowPreview = useCanShowPreview();

  // 選択中メール (?emailId と同期)。プレビューPaneに表示する。
  const selectedEmailId = searchParams.get("emailId");
  const selectedEmail = useMemo(
    () => activeEmails.nodes.find(node => node.id === selectedEmailId) ?? null,
    [activeEmails.nodes, selectedEmailId],
  );
  const showPreview = canShowPreview && !!selectedEmail && !isLoading;

  // 検索フィルターの表示/非表示状態。プレビュー表示中 (選択あり) は初期で閉じる。
  const [isSearchFilterOpen, setIsSearchFilterOpen] = useState(
    !selectedEmailId,
  );

  // Layout の max-w-7xl 制約を外し、サイドバー右端からビューポート右端まで全幅使用する
  useEffect(() => {
    const container = document.querySelector(
      "#layout-content-scroll > div",
    ) as HTMLElement | null;
    if (!container) return;
    container.classList.remove("max-w-7xl", "mx-auto");
    container.style.maxWidth = "none";
    return () => {
      container.classList.add("max-w-7xl", "mx-auto");
      container.style.maxWidth = "";
    };
  }, []);

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const favoriteCount = useMemo(
    () =>
      activeEmails.nodes.filter((node: EmailWithStatus) => node.favorite)
        .length,
    [activeEmails.nodes],
  );

  // 選択中メールIDを ?emailId に反映する
  const updateSelectedEmailId = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (id) {
        next.set("emailId", id);
      } else {
        next.delete("emailId");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // 検索結果クリック: 広い画面ではプレビュー選択、狭い画面では全画面遷移
  const handleSelectEmail = useCallback(
    (emailId: string): void => {
      if (!canShowPreview) {
        onOpenDetail(emailId);
        return;
      }
      onMarkRead(emailId);
      updateSelectedEmailId(emailId);
      setIsSearchFilterOpen(false);
    },
    [canShowPreview, onOpenDetail, onMarkRead, updateSelectedEmailId],
  );

  // プレビューを閉じる → 検索条件Paneを再表示
  const handleClosePreview = useCallback(() => {
    updateSelectedEmailId(null);
    setIsSearchFilterOpen(true);
  }, [updateSelectedEmailId]);

  // プレビューから全画面詳細へ卒業
  const handleOpenDetailFromPreview = useCallback(
    (panel?: "reply") => {
      if (selectedEmailId) {
        onOpenDetail(selectedEmailId, panel);
      }
    },
    [selectedEmailId, onOpenDetail],
  );

  // 検索フィルターの表示/非表示を切り替え
  const handleToggleSearchFilter = useCallback(() => {
    setIsSearchFilterOpen(prev => !prev);
  }, []);

  // 検索フィルターを閉じる（バツボタン用）
  const handleCloseSearchFilter = useCallback(() => {
    setIsSearchFilterOpen(false);
  }, []);

  // ↑↓ / J K で選択を移動し、プレビューを連動して切り替える（プレビュー表示中のみ）
  useEffect(() => {
    if (!showPreview) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      const isDown = event.key === "ArrowDown" || event.key === "j";
      const isUp = event.key === "ArrowUp" || event.key === "k";
      if (!isDown && !isUp) return;
      const tag = (document.activeElement?.tagName ?? "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const nodes = activeEmails.nodes;
      const currentIndex = nodes.findIndex(node => node.id === selectedEmailId);
      if (currentIndex === -1) return;
      const nextIndex = currentIndex + (isDown ? 1 : -1);
      if (nextIndex < 0 || nextIndex >= nodes.length) return;
      event.preventDefault();
      const nextId = nodes[nextIndex].id;
      onMarkRead(nextId);
      updateSelectedEmailId(nextId);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    showPreview,
    activeEmails.nodes,
    selectedEmailId,
    onMarkRead,
    updateSelectedEmailId,
  ]);

  // 選択中カードをスクロール表示する (キーボード移動・詳細から戻った時の追従)
  const scrollCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedEmailId && scrollCardRef.current) {
      scrollCardRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [selectedEmailId]);

  // Pane の出現/消滅を方向付きスライドでアニメーションさせる
  // (検索フォーム=左へ / プレビュー=右へ。data-slide 属性で向きを指定)
  const [paneContainerRef] = useAutoAnimate<HTMLDivElement>(slidePaneAnimation);

  return (
    <div>
      <PageTitle>メール検索</PageTitle>
      <div ref={paneContainerRef} className="flex gap-4 h-[calc(100vh-150px)]">
        {isSearchFilterOpen && !isLoading && (
          <div className="w-[320px] shrink-0 h-full" data-slide="left">
            <SearchEmailsFormUI
              isLoading={isLoading}
              currentSearchInput={currentSearchInput}
              onSearch={onSearch}
              onClearFilter={onClearFilter}
              onClose={handleCloseSearchFilter}
              isSearchFilterOpen={isSearchFilterOpen}
            />
          </div>
        )}

        {isLoading ? (
          <div className="w-full flex justify-center pt-20">
            <Loading size="xl" />
          </div>
        ) : (
          <div className="flex flex-col min-w-0 flex-1 min-h-0 h-full">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full min-h-0">
              <div className="flex items-center justify-between p-4 shrink-0">
                <div className="text-base-content flex items-end">
                  <SearchFilterButton
                    onClick={handleToggleSearchFilter}
                    isSearchFilterOpen={isSearchFilterOpen}
                  />
                  {activeEmails.nodes.length > 0 && (
                    <SearchEmailStats
                      totalCount={activeEmails.totalCount}
                      displayCount={activeEmails.nodes.length}
                      invisibleCount={invisibleEmailNums}
                      favoriteCount={favoriteCount}
                    />
                  )}
                </div>
                {activeEmails.nodes.length > 0 && (
                  <Toggle
                    label={viewModeLabel}
                    size="sm"
                    color="primary"
                    checked={viewMode === "table"}
                    onChange={event =>
                      setViewMode(event.target.checked ? "table" : "card")
                    }
                  />
                )}
              </div>
              <div className="w-full overflow-auto flex-1 min-h-0">
                {viewMode === "card" ? (
                  <EmailsCardsUI
                    isMatter={isMatter}
                    emails={isMatter ? matterEmails.nodes : hrEmails.nodes}
                    onSelectEmail={handleSelectEmail}
                    onOpenDetail={onOpenDetail}
                    onToggleFavoriteEmail={onToggleFavoriteEmail}
                    currentSearchInput={currentSearchInput}
                    scrollCardRef={scrollCardRef}
                    scrollCardId={selectedEmailId}
                    selectedId={selectedEmailId}
                  />
                ) : (
                  <EmailsTableUI
                    isMatter={isMatter}
                    emails={isMatter ? matterEmails.nodes : hrEmails.nodes}
                    onSelectEmail={handleSelectEmail}
                    onSelectContextMenu={onSelectContextMenu}
                    onToggleFavoriteEmail={onToggleFavoriteEmail}
                    freeWords={currentSearchInput.freeWords || []}
                    selectedId={selectedEmailId}
                  />
                )}
              </div>
            </div>

            {((isMatter && matterEmails.totalCount > SEARCH_LIMIT) ||
              (!isMatter && hrEmails.totalCount > SEARCH_LIMIT)) && (
              <div className="flex justify-center space-x-5 mb-20 mt-4">
                <Button width="narrow" onClick={onShowMore}>
                  もっと見る
                </Button>
              </div>
            )}
          </div>
        )}

        {showPreview && selectedEmail && (
          <div className="w-[460px] shrink-0 h-full" data-slide="right">
            <EmailPreviewPane
              email={selectedEmail}
              freeWords={currentSearchInput.freeWords || []}
              onClose={handleClosePreview}
              onOpenDetail={handleOpenDetailFromPreview}
            />
          </div>
        )}
      </div>
    </div>
  );
};
export default SearchEmailsPage;
